/**
 * Phase 1.1 Ship 3 Layer 4 — Admin repair page for broken generic CTAs.
 * Per auditor: "keep it simple. Admin tool should list/fix generic CTAs
 * where generated_url IS NULL OR qr_code_url IS NULL. No cron yet."
 */

import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  findAndRepairBrokenGenericCTAs,
  type RepairEventResult,
} from "../../services/eventCTA.service";
import { useToast } from "../../components/ui/Toast";
import Button from "../../components/common/Button";

const STATUS_LABEL: Record<
  RepairEventResult["qr"] | RepairEventResult["flyer"],
  string
> = {
  regenerated: "Rebuilt",
  ok: "OK",
  replaced: "Replaced",
  current: "Up to date",
  failed: "FAILED",
  artwork_pending: "Waiting for artwork",
  not_checked: "Not checked",
};

const RepairGenericCTAs: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [progress, setProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const [result, setResult] = useState<{
    candidateEvents: number;
    regeneratedTotal: number;
    failedTotal: number;
    flyersRefreshed: number;
    flyersFailed: number;
    flyersStillStale: number;
    flyersArtworkPending: number;
    perEvent: RepairEventResult[];
  } | null>(null);
  const [running, setRunning] = useState(false);

  const handleRepair = async () => {
    if (!user?.id) {
      showToast("error", "Sign in required");
      return;
    }
    setRunning(true);
    setProgress(null);
    setResult(null);
    try {
      const r = await findAndRepairBrokenGenericCTAs(user.id, (cur, total) =>
        setProgress({ current: cur, total }),
      );
      setResult(r);
      if (r.candidateEvents === 0) {
        showToast(
          "success",
          "No broken generic CTAs or out-of-date flyers found",
        );
      } else if (r.failedTotal === 0) {
        showToast(
          "success",
          `Repaired ${r.candidateEvents} events: ${r.regeneratedTotal} QR codes, ${r.flyersRefreshed} flyers`,
        );
      } else {
        showToast(
          "info",
          `Repaired ${r.regeneratedTotal}, ${r.failedTotal} failed (see details below)`,
        );
      }
    } catch (err) {
      console.error("Repair failed:", err);
      showToast("error", err instanceof Error ? err.message : "Repair failed");
    } finally {
      setRunning(false);
      setProgress(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Repair Generic CTAs
        </h1>
        <p className="text-sm text-gray-600 mt-2">
          The system auto-mints a fallback "Generic — no rep attribution" CTA on
          every subscription-eligible event. If QR generation failed at create
          time, this page finds + regenerates them. It also replaces any saved
          "Scan to join" flyer of an upcoming event that no longer matches its
          CTA (for example after a QR code was rebuilt), and re-checks
          afterwards. Idempotent — re-running on a clean set produces no
          changes, and anything that failed is picked up again on the next run.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow p-4 space-y-4">
        <Button variant="primary" onClick={handleRepair} disabled={running}>
          {running ? "Repairing…" : "Scan + repair broken generic CTAs"}
        </Button>

        {progress && (
          <div className="bg-blue-50 border border-blue-200 rounded p-2 text-sm text-blue-900">
            Progress: {progress.current} / {progress.total} events
          </div>
        )}

        {result && (
          <div className="space-y-2 text-sm">
            <p className="font-medium text-gray-900">Scan complete:</p>
            <ul className="list-disc list-inside text-gray-700">
              <li>{result.candidateEvents} candidate events</li>
              <li>{result.regeneratedTotal} QRs regenerated</li>
              <li>{result.flyersRefreshed} out-of-date flyers replaced</li>
              {result.flyersStillStale > 0 && (
                <li className="text-red-700">
                  {result.flyersStillStale} flyers still out of date — run again
                  or open the event
                </li>
              )}
              {result.flyersArtworkPending > 0 && (
                <li
                  className="text-amber-800"
                  data-testid="repair-artwork-pending"
                >
                  {result.flyersArtworkPending} insurance-state flyers with no
                  insurance design for their event type (only range nights and
                  gun shows have one) — not a failure: no new flyer was made,
                  and the event&apos;s PREVIOUS flyer is still in its Files. If
                  it shows a dollar amount, remove it from the event&apos;s
                  Files.
                </li>
              )}
              {result.failedTotal > 0 && (
                <li className="text-red-700">
                  {result.failedTotal} events with a failure
                </li>
              )}
            </ul>
            {result.perEvent.length > 0 && (
              <div className="border border-gray-200 rounded mt-3 max-h-96 overflow-y-auto">
                <table
                  className="w-full text-xs"
                  data-testid="repair-per-event"
                >
                  <thead className="bg-gray-50 text-gray-700">
                    <tr>
                      <th className="text-left p-2">Event</th>
                      <th className="text-left p-2">QR code</th>
                      <th className="text-left p-2">Flyer</th>
                      <th className="text-left p-2">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.perEvent.map((p) => (
                      <tr
                        key={p.eventId}
                        className={
                          p.error ? "bg-red-50 text-red-800" : "text-gray-800"
                        }
                      >
                        <td className="p-2 font-mono">
                          <a
                            className="underline"
                            href={`/events/${p.eventId}`}
                          >
                            {p.eventId.slice(0, 8)}
                          </a>
                        </td>
                        <td className="p-2">{STATUS_LABEL[p.qr]}</td>
                        <td className="p-2">{STATUS_LABEL[p.flyer]}</td>
                        <td className="p-2">{p.error ?? ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RepairGenericCTAs;
